import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  ListGroup,
  Badge,
  Modal,
  Form,
  Alert,
} from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import { usersApiService } from '../../services/apiServices';
import { UserWithRolesDto, UserStatus, AuthMethodDto, AuthMethodType } from '../../api-client';
import { getRoleNameRu } from '../../config/roleNames';
import { ApiError } from '../../api-client/core/ApiError';

const UserDetailPage: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const [user, setUser] = useState<UserWithRolesDto | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [showBlockModal, setShowBlockModal] = useState<boolean>(false);
  const [showRoleModal, setShowRoleModal] = useState<boolean>(false);
  const [selectedRole, setSelectedRole] = useState<string>('');
  const [showUnassignRoleModal, setShowUnassignRoleModal] = useState<boolean>(false);
  const [roleToUnassign, setRoleToUnassign] = useState<string>('');
  const [showAddPasswordAuthModal, setShowAddPasswordAuthModal] = useState<boolean>(false);
  const [showRoleAlreadyAssignedError, setShowRoleAlreadyAssignedError] = useState<boolean>(false);
  const [showProfileModal, setShowProfileModal] = useState<boolean>(false);
  const [fullNameInput, setFullNameInput] = useState<string>('');
  const [isSavingProfile, setIsSavingProfile] = useState<boolean>(false);

  const loadUser = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await usersApiService.getUserById(userId!);
      setUser(response.data);
    } catch (err: any) {
      console.error('Ошибка при загрузке пользователя:', err);
      setError(err.message || 'Ошибка при загрузке пользователя');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (userId) {
      loadUser();
    }
  }, [userId, loadUser]);

  const handleBlockUser = async () => {
    try {
      await usersApiService.blockUser(userId!);
      // Обновляем информацию о пользователе
      loadUser();
      setShowBlockModal(false);
    } catch (err: any) {
      console.error('Ошибка при блокировке пользователя:', err);
      setError(err.message || 'Ошибка при блокировке пользователя');
    }
  };

  const handleAssignRole = async () => {
    if (!selectedRole) return;

    try {
      await usersApiService.assignRoleToUser(userId!, selectedRole as any);
      // Обновляем информацию о пользователе
      loadUser();
      setShowRoleModal(false);
      setSelectedRole('');
    } catch (err: any) {
      console.error('Ошибка при назначении роли:', err);
      // Проверяем, является ли ошибка ошибкой "роль уже назначена"
      if (
        err instanceof ApiError &&
        err.status === 409 &&
        err.body?.code === 'user_role_already_assigned'
      ) {
        setShowRoleAlreadyAssignedError(true);
        setShowRoleModal(false);
      } else {
        setError(err.message || 'Ошибка при назначении роли');
      }
    }
  };

  const handleUnassignRole = async () => {
    if (!roleToUnassign) return;

    try {
      await usersApiService.removeRoleFromUser(userId!, roleToUnassign as any);
      // Обновляем информацию о пользователе
      loadUser();
      setShowUnassignRoleModal(false);
      setRoleToUnassign('');
    } catch (err: any) {
      console.error('Ошибка при удалении роли:', err);
      setError(err.message || 'Ошибка при удалении роли');
    }
  };

  const handleStatusChange = async (status: UserStatus) => {
    try {
      await usersApiService.updateUserStatus(userId!, status);
      // Обновляем информацию о пользователе
      loadUser();
    } catch (err: any) {
      console.error('Ошибка при изменении статуса:', err);
      setError(err.message || 'Ошибка при изменении статуса');
    }
  };

  const handleAddPasswordAuth = async () => {
    try {
      await usersApiService.addPasswordAuth(userId!);
      // Обновляем информацию о пользователе
      loadUser();
      setShowAddPasswordAuthModal(false);
    } catch (err: any) {
      console.error('Ошибка при добавлении аутентификации через пароль:', err);
      setError(err.message || 'Ошибка при добавлении аутентификации  через пароль');
    }
  };

  const hasPasswordAuth = (): boolean => {
    return (
      user?.authentication_methods?.some((method) => method.type === AuthMethodType.PASSWORD) ??
      false
    );
  };

  const handleOpenProfileModal = useCallback(() => {
    setFullNameInput(user?.profile?.full_name || '');
    setShowProfileModal(true);
  }, [user]);

  const handleSaveProfile = async () => {
    if (!fullNameInput.trim()) return;

    try {
      setIsSavingProfile(true);
      await usersApiService.fillUserProfile(userId!, fullNameInput.trim());
      await loadUser();
      setShowProfileModal(false);
    } catch (err: any) {
      console.error('Ошибка при сохранении профиля:', err);
      setError(err.message || 'Ошибка при сохранении профиля');
    } finally {
      setIsSavingProfile(false);
    }
  };

  const getAuthMethodDisplayName = (method: AuthMethodDto): string => {
    switch (method.type) {
      case AuthMethodType.PASSWORD:
        return 'Пароль';
      case AuthMethodType.TELEGRAM:
        const data = method.data as any;
        return `Telegram (${data.telegram_username || data.telegram_id})`;
      default:
        return method.type;
    }
  };

  if (loading) {
    return (
      <Container className="text-center my-5">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Загрузка...</span>
        </div>
        <p className="mt-2">Загрузка информации о пользователе...</p>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="mt-4">
        <Alert variant="danger">{error}</Alert>
        <Button onClick={() => navigate('/users')}>Назад к списку</Button>
      </Container>
    );
  }

  if (!user) {
    return (
      <Container className="mt-4">
        <Alert variant="warning">Пользователь не найден</Alert>
        <Button onClick={() => navigate('/users')}>Назад к списку</Button>
      </Container>
    );
  }

  return (
    <Container fluid>
      <Row>
        <Col>
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h2>Информация о пользователе</h2>
            <Button variant="secondary" onClick={() => navigate('/users')}>
              Назад к списку
            </Button>
          </div>

          {error && <Alert variant="danger">{error}</Alert>}

          <Row>
            <Col md={8}>
              <Card>
                <Card.Header>
                  <Card.Title>Основная информация</Card.Title>
                </Card.Header>
                <Card.Body>
                  <ListGroup variant="flush">
                    <ListGroup.Item>
                      <strong>ID:</strong> {user.id}
                    </ListGroup.Item>
                    <ListGroup.Item>
                      <strong>Email:</strong> {user.email}
                    </ListGroup.Item>
                    <ListGroup.Item>
                      <strong>ФИО:</strong>{' '}
                      {user.profile?.full_name || <span className="text-muted">Не заполнено</span>}{' '}
                      <Button
                        variant="outline-primary"
                        size="sm"
                        className="ms-2"
                        onClick={handleOpenProfileModal}
                      >
                        {user.profile?.full_name ? 'Редактировать' : 'Заполнить'}
                      </Button>
                    </ListGroup.Item>
                    <ListGroup.Item>
                      <strong>Имя пользователя:</strong> {user.username}
                    </ListGroup.Item>
                    <ListGroup.Item>
                      <strong>Статус:</strong>{' '}
                      <Badge
                        bg={
                          user.status === UserStatus.ACTIVE
                            ? 'success'
                            : user.status === UserStatus.PENDING
                              ? 'warning'
                              : 'danger'
                        }
                      >
                        {user.status === UserStatus.ACTIVE
                          ? 'Активный'
                          : user.status === UserStatus.PENDING
                            ? 'Ожидание'
                            : 'Заблокирован'}
                      </Badge>
                    </ListGroup.Item>
                    <ListGroup.Item>
                      <strong>Дата регистрации:</strong>{' '}
                      {new Date(user.created_at).toLocaleString()}
                    </ListGroup.Item>
                    <ListGroup.Item>
                      <strong>Дата подтверждения email:</strong>{' '}
                      {user.email_verified_at
                        ? new Date(user.email_verified_at).toLocaleString()
                        : 'Не подтвержден'}
                    </ListGroup.Item>
                  </ListGroup>
                </Card.Body>
              </Card>
            </Col>

            <Col md={4}>
              <Card>
                <Card.Header>
                  <Card.Title>Действия</Card.Title>
                </Card.Header>
                <Card.Body>
                  <div className="d-grid gap-2">
                    <Button
                      variant="success"
                      onClick={() => handleStatusChange(UserStatus.ACTIVE)}
                      disabled={user.status === UserStatus.ACTIVE}
                    >
                      Сделать активным
                    </Button>

                    <Button
                      variant="warning"
                      onClick={() => handleStatusChange(UserStatus.PENDING)}
                      disabled={user.status === UserStatus.PENDING}
                    >
                      Установить статус "Ожидание"
                    </Button>

                    <Button
                      variant="danger"
                      onClick={() => setShowBlockModal(true)}
                      disabled={user.status === UserStatus.BLOCKED}
                    >
                      Заблокировать
                    </Button>

                    <Button variant="info" onClick={() => setShowRoleModal(true)}>
                      Назначить роль
                    </Button>

                    {!hasPasswordAuth() && (
                      <Button variant="primary" onClick={() => setShowAddPasswordAuthModal(true)}>
                        Открыть доступ в админ панель
                      </Button>
                    )}
                  </div>
                </Card.Body>
              </Card>

              <Card className="mt-3">
                <Card.Header>
                  <Card.Title>Роли пользователя</Card.Title>
                </Card.Header>
                <Card.Body>
                  {user.roles && user.roles.length > 0 ? (
                    <ListGroup>
                      {user.roles
                        .slice()
                        .sort((a, b) => getRoleNameRu(a.name).localeCompare(getRoleNameRu(b.name)))
                        .map((role, index) => (
                          <ListGroup.Item
                            key={index}
                            className="d-flex justify-content-between align-items-center"
                          >
                            <Badge bg="info">{getRoleNameRu(role.name)}</Badge>
                            <small className="text-muted">
                              Назначена: {new Date(role.assigned_at).toLocaleDateString()}
                            </small>
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => {
                                setRoleToUnassign(role.name);
                                setShowUnassignRoleModal(true);
                              }}
                            >
                              Удалить
                            </Button>
                          </ListGroup.Item>
                        ))}
                    </ListGroup>
                  ) : (
                    <p>У пользователя пока нет ролей</p>
                  )}
                </Card.Body>
              </Card>

              <Card className="mt-3">
                <Card.Header>
                  <Card.Title>Методы аутентификации</Card.Title>
                </Card.Header>
                <Card.Body>
                  {user.authentication_methods && user.authentication_methods.length > 0 ? (
                    <ListGroup>
                      {user.authentication_methods.map((method, index) => (
                        <ListGroup.Item key={index}>
                          <Badge
                            bg={method.type === AuthMethodType.PASSWORD ? 'primary' : 'secondary'}
                          >
                            {getAuthMethodDisplayName(method)}
                          </Badge>
                        </ListGroup.Item>
                      ))}
                    </ListGroup>
                  ) : (
                    <p>У пользователя пока нет методов аутентификации</p>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Col>
      </Row>

      {/* Модальное окно блокировки */}
      <Modal show={showBlockModal} onHide={() => setShowBlockModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Подтвердите блокировку</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Вы уверены, что хотите заблокировать пользователя <strong>{user.username}</strong>?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowBlockModal(false)}>
            Отмена
          </Button>
          <Button variant="danger" onClick={handleBlockUser}>
            Заблокировать
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Модальное окно редактирования профиля */}
      <Modal show={showProfileModal} onHide={() => setShowProfileModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>
            {user?.profile?.full_name ? 'Редактировать профиль' : 'Заполнить профиль'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group>
              <Form.Label>ФИО</Form.Label>
              <Form.Control
                type="text"
                value={fullNameInput}
                onChange={(e) => setFullNameInput(e.target.value)}
                placeholder="Введите ФИО пользователя"
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowProfileModal(false)}>
            Отмена
          </Button>
          <Button
            variant="primary"
            onClick={handleSaveProfile}
            disabled={!fullNameInput.trim() || isSavingProfile}
          >
            {isSavingProfile ? 'Сохранение...' : 'Сохранить'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Модальное окно назначения роли */}
      <Modal show={showRoleModal} onHide={() => setShowRoleModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Назначить роль</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group>
              <Form.Label>Выберите роль:</Form.Label>
              <Form.Select value={selectedRole} onChange={(e) => setSelectedRole(e.target.value)}>
                <option value="">Выберите роль</option>
                <option value="admin">Администратор</option>
                <option value="accountant">Бухгалтер</option>
                <option value="hr">HR</option>
                <option value="lawyer">Юрист</option>
                <option value="recruiter">Рекрутер</option>
                <option value="user">Пользователь</option>
              </Form.Select>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowRoleModal(false)}>
            Отмена
          </Button>
          <Button variant="primary" onClick={handleAssignRole} disabled={!selectedRole}>
            Назначить
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Модальное окно удаления роли */}
      <Modal show={showUnassignRoleModal} onHide={() => setShowUnassignRoleModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Подтвердите удаление роли</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Вы уверены, что хотите удалить роль{' '}
          <strong>{getRoleNameRu(roleToUnassign as any)}</strong> у пользователя?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowUnassignRoleModal(false)}>
            Отмена
          </Button>
          <Button variant="danger" onClick={handleUnassignRole}>
            Удалить
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Модальное окно добавления аутентификации через пароль */}
      <Modal show={showAddPasswordAuthModal} onHide={() => setShowAddPasswordAuthModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Открыть доступ в админ панель</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Пользователю <strong>{user?.username}</strong> будет отправлен пароль на электронную почту{' '}
          <strong>{user?.email}</strong>.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAddPasswordAuthModal(false)}>
            Отмена
          </Button>
          <Button variant="primary" onClick={handleAddPasswordAuth}>
            Открыть доступ
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Модальное окно ошибки - роль уже назначена */}
      <Modal
        show={showRoleAlreadyAssignedError}
        onHide={() => setShowRoleAlreadyAssignedError(false)}
      >
        <Modal.Header closeButton>
          <Modal.Title>Ошибка</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          У пользователя уже есть роль <strong>{getRoleNameRu(selectedRole as any)}</strong>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={() => setShowRoleAlreadyAssignedError(false)}>
            Закрыть
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default UserDetailPage;
